import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Container, IconButton, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import UploadIcon from '@mui/icons-material/Upload';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../core/PageHeader';
import { ErrorTryRefetch } from '../../core/ErrorTryRefetch';
import { useAlert } from '../../core/AlertContext';
import { useBlogPost, useCreateBlogPost, useUpdateBlogPost, useUploadBlogImages } from '../blogHooks';
import { useGetTags } from '../../hooks/tags';
import { blogPostCreateInputSchema } from '../blogSchemas';
import { canCreateBlogPost, canEditBlogPost } from '../blogPermissions';
import { parseApiErrorMessage } from '../blogUtils';
import DetailScreenHeader from '../../core/DetailScreenHeader';
import { primaryButtonSx } from '../../core/pvtaStyles';

const BlogEditorPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const postId = Number(id ?? 0);
    const isEditMode = Number.isFinite(postId) && postId > 0;
    const { showAlert } = useAlert();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { data: post, isLoading, isError, refetch } = useBlogPost(postId);
    const createMutation = useCreateBlogPost();
    const updateMutation = useUpdateBlogPost();
    const uploadImagesMutation = useUploadBlogImages();
    const { tags } = useGetTags();

    const [title, setTitle] = useState('');
    const [teaser, setTeaser] = useState('');
    const [content, setContent] = useState('');
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!post || !isEditMode) {
            return;
        }
        setTitle(post.title);
        setTeaser(post.teaser);
        setContent(post.content);
        setSelectedTagIds(post.tags.map((tag) => tag.id));
    }, [isEditMode, post]);

    const toggleTag = (tagId: number): void => {
        setSelectedTagIds((previous) =>
            previous.includes(tagId) ? previous.filter((id) => id !== tagId) : [...previous, tagId]
        );
    };

    const isSaving = createMutation.isPending || updateMutation.isPending || uploadImagesMutation.isPending;

    const handleSelectFiles = ({ target }: ChangeEvent<HTMLInputElement>): void => {
        const files = Array.from(target.files ?? []);
        if (files.length > 0) {
            setSelectedFiles((previous) => [...previous, ...files]);
        }
        target.value = '';
    };

    if (!isEditMode && !canCreateBlogPost()) {
        return (
            <ErrorTryRefetch
                refetchFunction={() => {
                    navigate('/blog');
                }}
            />
        );
    }

    if (isEditMode && post && !canEditBlogPost(post.author.id)) {
        return (
            <ErrorTryRefetch
                refetchFunction={() => {
                    navigate(`/blog/${postId}`);
                }}
            />
        );
    }

    if (isEditMode && isError) {
        return <ErrorTryRefetch refetchFunction={() => void refetch()} />;
    }

    const handleSave = async (): Promise<void> => {
        const parsed = blogPostCreateInputSchema.safeParse({ title, teaser, content, tag_ids: selectedTagIds });
        if (!parsed.success) {
            showAlert({ severity: 'error', text: parsed.error.issues[0]?.message ?? 'Ungültige Eingaben.' });
            return;
        }

        try {
            if (isEditMode) {
                const updated = await updateMutation.mutateAsync({
                    id: postId,
                    ...parsed.data,
                });
                if (selectedFiles.length > 0) {
                    await uploadImagesMutation.mutateAsync({ postId: updated.id, files: selectedFiles });
                    setSelectedFiles([]);
                }
                showAlert({ severity: 'success', text: 'Beitrag aktualisiert.' });
                navigate(`/blog/${updated.id}`);
                return;
            }

            const created = await createMutation.mutateAsync(parsed.data);
            if (selectedFiles.length > 0) {
                await uploadImagesMutation.mutateAsync({ postId: created.id, files: selectedFiles });
                setSelectedFiles([]);
            }
            showAlert({ severity: 'success', text: 'Beitrag erstellt.' });
            navigate(`/blog/${created.id}`);
        } catch (error) {
            showAlert({ severity: 'error', text: parseApiErrorMessage(error, 'Beitrag konnte nicht gespeichert werden.') });
        }
    };

    return (
        <Box
            sx={{
                py: { xs: 0, md: 4 },
                minHeight: { xs: '100dvh', md: 'auto' },
                backgroundColor: { xs: 'background.paper', md: 'transparent' },
            }}
        >
            <Container
                maxWidth="md"
                disableGutters={isMobile}
                sx={{ px: { xs: 0, md: 3 } }}
            >
                {isMobile ? (
                    <DetailScreenHeader
                        title={isEditMode ? 'Beitrag bearbeiten' : 'Neuer Beitrag'}
                        onClose={() => navigate(isEditMode ? `/blog/${postId}` : '/blog')}
                        actions={(
                            <IconButton
                                aria-label="blog speichern"
                                onClick={() => void handleSave()}
                                disabled={isSaving || (isEditMode && isLoading)}
                                sx={{
                                    minHeight: 44,
                                    minWidth: 44,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <SaveIcon />
                            </IconButton>
                        )}
                    />
                ) : (
                    <PageHeader title={isEditMode ? 'Beitrag bearbeiten' : 'Neuer Beitrag'} />
                )}
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: { xs: 0, md: 1.25 },
                        borderWidth: { xs: 0, md: 1 },
                        boxShadow: { xs: 'none', md: undefined },
                    }}
                >
                    <CardContent>
                        <Stack spacing={1.5}>
                            <TextField
                                label="Titel"
                                value={title}
                                onChange={({ target }) => setTitle(target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Inhalt"
                                value={content}
                                onChange={({ target }) => setContent(target.value)}
                                fullWidth
                                multiline
                                minRows={10}
                            />
                            <Box
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1.5,
                                    px: 1.5,
                                    py: 1.25,
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                    Tags
                                </Typography>
                                {tags.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Noch keine Tags vorhanden. Lege welche unter Einstellungen → Tags an.
                                    </Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {tags.map((tag) => {
                                            const active = selectedTagIds.includes(tag.id);
                                            return (
                                                <Chip
                                                    key={tag.id}
                                                    label={tag.name}
                                                    onClick={() => toggleTag(tag.id)}
                                                    sx={{
                                                        borderRadius: 999,
                                                        fontWeight: 600,
                                                        cursor: 'pointer',
                                                        backgroundColor: active ? tag.color : 'transparent',
                                                        color: active ? '#ffffff' : 'inherit',
                                                        border: '1px solid',
                                                        borderColor: active ? tag.color : 'divider',
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>
                            <Box
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1.5,
                                    px: 1.5,
                                    py: 1.25,
                                }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Bilder
                                    </Typography>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        hidden
                                        multiple
                                        accept="image/*"
                                        onChange={handleSelectFiles}
                                    />
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<UploadIcon fontSize="small" />}
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isSaving}
                                    >
                                        Bilder wählen
                                    </Button>
                                </Stack>
                                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                    {selectedFiles.length === 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Keine Bilder ausgewählt.
                                        </Typography>
                                    )}
                                    {selectedFiles.map((file, index) => (
                                        <Chip
                                            key={`${file.name}-${index}`}
                                            size="small"
                                            label={file.name}
                                            onDelete={isSaving ? undefined : () => {
                                                setSelectedFiles((previous) => previous.filter((_file, itemIndex) => itemIndex !== index));
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                            <Stack direction="row" justifyContent="flex-end" spacing={1}>
                                <Button variant="text" onClick={() => navigate(isEditMode ? `/blog/${postId}` : '/blog')}>
                                    Abbrechen
                                </Button>
                                <Button
                                    sx={{ ...primaryButtonSx }}
                                    onClick={() => void handleSave()}
                                    disabled={isSaving || (isEditMode && isLoading)}
                                >
                                    Speichern
                                </Button>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default BlogEditorPage;
